import { PastPaper, SYLLABUS_CATALOG, SyllabusUnit, normalizeSyllabusTitle } from '@study-tracker/domain';
import { deterministicUUID } from '../utils/uuid';
import { IDatabase } from '../interfaces/IDatabase';
import { createDefaultSeedData } from './seedData';

const seedMetadata = () => ({
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  deletedAt: null,
  version: 1,
  originDeviceId: 'system-seed',
  schemaVersion: 1,
});

const paperTypes = ['PAPER_I', 'PAPER_II'] as const;

export async function ensureSyllabusCatalog(db: IDatabase): Promise<void> {
  const defaults = createDefaultSeedData();
  let subjects = await db.subjects.findAll();
  const missingSubjects = defaults.subjects.filter((subject) => !subjects.some((current) => current.code === subject.code));
  if (missingSubjects.length) {
    await db.subjects.upsertBatch(missingSubjects);
    subjects = await db.subjects.findAll();
  }

  let units = await db.syllabusUnits.findAllIncludingDeleted();
  const unitsToWrite: SyllabusUnit[] = units
    .filter((unit) => unit.deletedAt === null && unit.status === undefined)
    .map((unit) => ({
      ...unit,
      status: 'NOT_STARTED',
      updatedAt: new Date().toISOString(),
      version: unit.version + 1,
    }));
  for (const catalogSubject of SYLLABUS_CATALOG) {
    const subject = subjects.find((candidate) => candidate.code === catalogSubject.code);
    if (!subject) continue;
    const subjectUnits = units.filter((unit) => unit.subjectId === subject.id && unit.deletedAt === null);
    let nextCustomNumber = Math.max(catalogSubject.units.length, ...subjectUnits.map((unit) => unit.unitNumber)) + 1;

    for (const [index, title] of catalogSubject.units.entries()) {
      const number = index + 1;
      const normalizedTitle = normalizeSyllabusTitle(title);
      let match = subjectUnits.find((unit) => normalizeSyllabusTitle(unit.title) === normalizedTitle);
      if (!match) {
        match = subjectUnits.find((unit) => unit.unitNumber === number && unit.originDeviceId === 'system-seed');
        if (match) {
          match = { ...match, title, sortOrder: index };
          unitsToWrite.push(match);
        }
      }

      if (match) {
        if (match.unitNumber !== number && match.originDeviceId === 'system-seed') {
          const normalized = { ...match, unitNumber: number, sortOrder: index };
          unitsToWrite.push(normalized);
          match = normalized;
        }
        continue;
      }

      for (const conflict of subjectUnits.filter((unit) => unit.unitNumber === number && unit.originDeviceId !== 'system-seed')) {
        const moved: SyllabusUnit = { ...conflict, unitNumber: nextCustomNumber, sortOrder: nextCustomNumber - 1 };
        nextCustomNumber++;
        unitsToWrite.push(moved);
        units = units.map((unit) => unit.id === moved.id ? moved : unit);
      }

      const canonicalId = deterministicUUID(`UNIT_${catalogSubject.code}_${number}`);
      const byId = subjectUnits.find((unit) => unit.id === canonicalId);
      if (byId) {
        continue;
      }
      const canonical: SyllabusUnit = {
        ...seedMetadata(), id: canonicalId, subjectId: subject.id,
        unitNumber: number, title, status: 'NOT_STARTED', sortOrder: index,
      };
      unitsToWrite.push(canonical);
      subjectUnits.push(canonical);
    }
  }

  if (unitsToWrite.length) {
    const uniqueById = new Map(unitsToWrite.map((unit) => [unit.id, unit]));
    await db.syllabusUnits.upsertBatch([...uniqueById.values()]);
    units = await db.syllabusUnits.findAllIncludingDeleted();
  }

  const maths = subjects.find((subject) => subject.code === 'COMBINED_MATHS');
  if (maths) {
    const mathsUnits = units.filter((unit) => unit.subjectId === maths.id && unit.deletedAt === null);
    const differentiationUnit = mathsUnits.find((unit) => unit.unitNumber === 9);
    const integrationUnit = mathsUnits.find((unit) => unit.unitNumber === 10);
    const chapters = await db.chapters.findAll();
    const moves = chapters.flatMap((chapter) => {
      const target = chapter.title.toLocaleLowerCase().includes('integrat')
        ? integrationUnit
        : chapter.title.toLocaleLowerCase().includes('differentiat') ? differentiationUnit : undefined;
      return target && chapter.unitId !== target.id ? [{ ...chapter, unitId: target.id }] : [];
    });
    if (moves.length) await db.chapters.upsertBatch(moves);
  }

  const papers = await db.pastPapers.findAllIncludingDeleted();
  const paperByKey = new Set(papers.map((paper) => `${paper.subjectId}:${paper.year}:${paper.paperType}`));
  const missingPapers: PastPaper[] = [];
  for (const subject of subjects) {
    for (let year = 2010; year <= 2026; year++) {
      for (const paperType of paperTypes) {
        const key = `${subject.id}:${year}:${paperType}`;
        if (paperByKey.has(key)) continue;
        missingPapers.push({
          ...seedMetadata(),
          id: deterministicUUID(`PAST_PAPER_${subject.code}_${year}_${paperType}`),
          subjectId: subject.id, year, paperType, status: 'NOT_STARTED',
          difficulty: null, isRevisionRequired: false, score: null, notes: '', fileAttachmentUri: null,
        });
        paperByKey.add(key);
      }
    }
  }
  if (missingPapers.length) await db.pastPapers.upsertBatch(missingPapers);
}
